import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Plus, Car, Bike, Calendar, DollarSign, Star, Eye, Edit, CheckCircle
} from 'lucide-react'
import { useLanguageStore, useCurrencyStore } from '../../lib/store'
import { formatCurrency } from '../../lib/utils'

export default function MyVehiclesPage() {
    useTranslation()
    const { language } = useLanguageStore()
    useCurrencyStore()

    const [filter, setFilter] = useState<'all' | 'active' | 'inactive' | 'pending'>('all')

    // Mock data
    const vehicles = [
        {
            id: 1,
            title: { en: 'Honda Wave Alpha 2022', vi: 'Honda Wave Alpha 2022' },
            type: 'motorcycle',
            dailyRate: 150000,
            status: 'active',
            rating: 4.8,
            reviewCount: 45,
            totalBookings: 120,
            totalEarnings: 18000000,
            image: '/images/vehicles/wave.jpg',
            lastBooking: '2026-01-05',
        },
        {
            id: 2,
            title: { en: 'Honda Air Blade 160', vi: 'Honda Air Blade 160' },
            type: 'motorcycle',
            dailyRate: 200000,
            status: 'active',
            rating: 4.9,
            reviewCount: 32,
            totalBookings: 85,
            totalEarnings: 17000000,
            image: '/images/vehicles/airblade.jpg',
            lastBooking: '2026-01-07',
        },
        {
            id: 3,
            title: { en: 'Toyota Vios 2023', vi: 'Toyota Vios 2023' },
            type: 'car',
            dailyRate: 800000,
            status: 'inactive',
            rating: 4.7,
            reviewCount: 18,
            totalBookings: 35,
            totalEarnings: 28000000,
            image: '/images/vehicles/vios.jpg',
            lastBooking: '2025-12-28',
        },
    ]

    const stats = {
        totalVehicles: vehicles.length,
        activeVehicles: vehicles.filter(v => v.status === 'active').length,
        totalBookings: vehicles.reduce((sum, v) => sum + v.totalBookings, 0),
        totalEarnings: vehicles.reduce((sum, v) => sum + v.totalEarnings, 0),
    }

    const filteredVehicles = filter === 'all'
        ? vehicles
        : vehicles.filter(v => v.status === filter)

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold">{language === 'en' ? 'My Vehicles' : 'Xe Của Tôi'}</h1>
                        <p className="text-gray-600">{language === 'en' ? 'Manage your rental vehicles' : 'Quản lý xe cho thuê của bạn'}</p>
                    </div>
                    <Link
                        to="/owner/vehicles/add"
                        className="btn bg-primary-600 text-white hover:bg-primary-700"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        {language === 'en' ? 'Add Vehicle' : 'Thêm Xe'}
                    </Link>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="card">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Car className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Total Vehicles' : 'Tổng xe'}</p>
                                <p className="text-2xl font-bold">{stats.totalVehicles}</p>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Active' : 'Đang hoạt động'}</p>
                                <p className="text-2xl font-bold text-green-600">{stats.activeVehicles}</p>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-yellow-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Total Bookings' : 'Tổng đặt xe'}</p>
                                <p className="text-2xl font-bold">{stats.totalBookings}</p>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <DollarSign className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Total Earnings' : 'Tổng thu nhập'}</p>
                                <p className="text-xl font-bold">{formatCurrency(stats.totalEarnings, 'VND')}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex gap-2 mb-6">
                    {(['all', 'active', 'inactive', 'pending'] as const).map((status) => (
                        <button
                            key={status}
                            onClick={() => setFilter(status)}
                            className={`px-4 py-2 rounded-lg font-medium transition ${filter === status
                                ? 'bg-primary-600 text-white'
                                : 'bg-white hover:bg-gray-100'
                                }`}
                        >
                            {status === 'all' && (language === 'en' ? 'All' : 'Tất cả')}
                            {status === 'active' && (language === 'en' ? 'Active' : 'Hoạt động')}
                            {status === 'inactive' && (language === 'en' ? 'Inactive' : 'Tạm dừng')}
                            {status === 'pending' && (language === 'en' ? 'Pending' : 'Chờ duyệt')}
                        </button>
                    ))}
                </div>

                {/* Vehicles Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredVehicles.map((vehicle) => (
                        <div key={vehicle.id} className="card hover:shadow-lg transition">
                            <div className="relative">
                                <div className="aspect-[4/3] bg-gray-200 rounded-lg flex items-center justify-center mb-4">
                                    {vehicle.type === 'motorcycle' ? <Bike className="w-12 h-12 text-gray-400" /> : <Car className="w-12 h-12 text-gray-400" />}
                                </div>
                                <span className={`absolute top-2 right-2 badge ${vehicle.status === 'active' ? 'badge-success' : 'badge-gray'}`}>
                                    {vehicle.status === 'active' ? (language === 'en' ? 'Active' : 'Hoạt động') : (language === 'en' ? 'Inactive' : 'Tạm dừng')}
                                </span>
                            </div>

                            <h3 className="font-bold text-lg mb-1">{vehicle.title[language]}</h3>

                            <div className="flex items-center gap-2 mb-3">
                                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                <span className="font-medium">{vehicle.rating}</span>
                                <span className="text-gray-500">({vehicle.reviewCount} {language === 'en' ? 'reviews' : 'đánh giá'})</span>
                            </div>

                            <div className="flex items-center justify-between py-3 border-t border-b mb-3">
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">{language === 'en' ? 'Rate' : 'Giá'}</p>
                                    <p className="font-bold text-primary-600">{formatCurrency(vehicle.dailyRate, 'VND')}</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">{language === 'en' ? 'Bookings' : 'Lượt thuê'}</p>
                                    <p className="font-bold">{vehicle.totalBookings}</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">{language === 'en' ? 'Earnings' : 'Thu nhập'}</p>
                                    <p className="font-bold text-green-600">{(vehicle.totalEarnings / 1000000).toFixed(1)}M</p>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <Link
                                    to={`/vehicles/${vehicle.id}`}
                                    className="flex-1 py-2 text-center border rounded-lg hover:bg-gray-50 transition"
                                >
                                    <Eye className="w-4 h-4 inline mr-1" />
                                    {language === 'en' ? 'View' : 'Xem'}
                                </Link>
                                <Link
                                    to={`/owner/vehicles/${vehicle.id}/edit`}
                                    className="flex-1 py-2 text-center bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                                >
                                    <Edit className="w-4 h-4 inline mr-1" />
                                    {language === 'en' ? 'Edit' : 'Sửa'}
                                </Link>
                            </div>
                        </div>
                    ))}

                    {/* Add New Vehicle Card */}
                    <Link
                        to="/owner/vehicles/add"
                        className="card border-2 border-dashed hover:border-primary-500 hover:bg-primary-50 transition flex flex-col items-center justify-center min-h-[300px] text-gray-400 hover:text-primary-600"
                    >
                        <Plus className="w-12 h-12 mb-2" />
                        <p className="font-medium">{language === 'en' ? 'Add New Vehicle' : 'Thêm Xe Mới'}</p>
                    </Link>
                </div>
            </div>
        </div>
    )
}
